import type {ReactElement} from 'react';
import type {TabItems} from '@Components/tabs';
import {Tabs, TabSliderType, TabsThemes} from '@Components/tabs';
import {EventVenueType, getIsOnlineVenueComplete, isOfflineVenueComplete} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {VenueOffline} from '@Components/event-wizard/venue-offline';
import {VenueOnline} from '@Components/event-wizard/venue-online';
import {onEventWizardVenueTypeChange} from '@Components/event-wizard/event-wizard-slice';
import {useDispatch} from 'react-redux';
import {updatePreviewVenue} from '@Components/event-wizard/preview/preview.helper';
import {useEventWizardOfflineVenue, useEventWizardOnlineVenue, useEventWizardSelectedVenue} from '@Components/event-wizard/event-wizard.hooks';
import styles from './event-wizard-venue-content.module.scss';
import type {EventWizardVenueContentProps} from './event-wizard-venue-content.types';

export function EventWizardVenueContent({isVisible = true, isMobileView = false}: EventWizardVenueContentProps): ReactElement<any> {
  const dispatch = useDispatch();
  const selectedVenue = useEventWizardSelectedVenue();
  const offlineVenue = useEventWizardOfflineVenue();
  const onlineVenue = useEventWizardOnlineVenue();

  const updateVenueType = (venueType: string): void => {
    const selectedVenueType = venueType as EventVenueType;
    dispatch(onEventWizardVenueTypeChange(selectedVenueType));

    if (selectedVenueType === EventVenueType.OFFLINE && isOfflineVenueComplete(offlineVenue)) {
      updatePreviewVenue({offlineVenue});
    } else if (selectedVenueType === EventVenueType.ONLINE && getIsOnlineVenueComplete(onlineVenue)) {
      updatePreviewVenue({onlineVenueURL: onlineVenue});
    } else {
      updatePreviewVenue({selectedVenueType: undefined});
    }
  };

  const getVenueTabs = (): TabItems => {
    return [
      {
        tabKey: EventVenueType.OFFLINE,
        text: window.i18next.t('pmwjs_venue'),
        textClassName: 'body-md-bold',
        className: 'spacing-p-r-0 spacing-p-l-0 spacing-p-t-1 spacing-p-b-1',
      },
      {
        tabKey: EventVenueType.ONLINE,
        text: window.i18next.t('pmwjs_online'),
        textClassName: 'body-md-bold',
        className: 'spacing-p-r-0 spacing-p-l-0 spacing-p-t-1 spacing-p-b-1',
      },
    ];
  };

  return (
    <div
      className={`${!isVisible ? '_hidden' : ''} ${isMobileView ? 'spacing-p-t-3 spacing-p-l-6 spacing-p-r-6' : 'border-s-primary spacing-p-3'} ${styles.fadeIn} radius-8 _full-width`}
    >
      <Tabs
        id="VenueTabs"
        className="_full-width spacing-m-b-3"
        tabItems={getVenueTabs()}
        selectedTab={selectedVenue ?? EventVenueType.OFFLINE}
        onTabClick={updateVenueType}
        sliderType={TabSliderType.NONE}
        theme={TabsThemes.PRIMARY_LIGHT}
        shouldTabsHaveEqualWidth
        tabsContent={{
          [EventVenueType.OFFLINE]: <VenueOffline isMobile={isMobileView} />,
          [EventVenueType.ONLINE]: <VenueOnline />,
        }}
      />
    </div>
  );
}
